import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNotif } from '../context/NotifContext';
import './Topbar.css';

const MoonIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
const SunIcon  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;

const NOTIF_ICONS = { lead:'👤', invoice:'🧾', project:'📋', team:'👥', reminder:'⏰' };
const ROLE_COLOR  = { 'Super Admin':'#F5A623', 'Admin':'#3B82F6', 'User':'#22C55E' };

function NotifPanel({ notifs, markRead, markAll, onClose, theme }) {
  return (
    <div className="tb-notif-panel" data-theme={theme}>
      <div className="tb-np-hd">
        <span className="tb-np-title">Notifications</span>
        <span className="tb-np-clear" onClick={markAll}>Mark all read</span>
      </div>
      <div className="tb-np-list">
        {notifs.map(n => (
          <div key={n.id} className={`tb-np-item${n.read?'':' unread'}`} onClick={()=>markRead(n.id)}>
            <span className="tb-np-icon">{NOTIF_ICONS[n.type]||'📌'}</span>
            <div className="tb-np-body">
              <div className="tb-np-text">{n.text}</div>
              <div className="tb-np-time">{n.time}</div>
            </div>
            {!n.read && <div className="tb-np-dot"/>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfilePanel({ user, theme, onClose, navigate }) {
  return (
    <div className="tb-profile-panel" data-theme={theme}>
      <div className="tb-pp-top">
        <div className="tb-pp-av" style={{background:`${ROLE_COLOR[user.role]}22`,color:ROLE_COLOR[user.role]}}>{user.initials}</div>
        <div>
          <div className="tb-pp-name">{user.name}</div>
          <div className="tb-pp-email">{user.email}</div>
          <div className="tb-pp-role" style={{color:ROLE_COLOR[user.role]}}>{user.role}</div>
        </div>
      </div>
      <div className="tb-pp-divider"/>
      {[
        { icon:'⚙', label:'Settings', action:()=>{ navigate('/settings'); onClose(); } },
        { icon:'👤', label:'My Profile', action:()=>{ navigate('/settings'); onClose(); } },
        { icon:'🔔', label:'Notification Prefs', action:onClose },
        { icon:'🚪', label:'Sign Out', action:()=>{ import('../services/api').then(m=>m.clearToken()); navigate('/login'); onClose(); }, red:true },
      ].map(item => (
        <div key={item.label} className={`tb-pp-item${item.red?' red':''}`} onClick={item.action}>
          <span className="tb-pp-item-icon">{item.icon}</span>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function Topbar({ title, subtitle, actions }) {
  const { theme, toggle } = useTheme();
  const { user }          = useAuth();
  const { notifs, markRead, markAll, unread } = useNotif();
  const navigate          = useNavigate();
  const isDark            = theme === 'dark';

  const [showNotif,   setShowNotif]   = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const notifRef   = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current   && !notifRef.current.contains(e.target))   setShowNotif(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="topbar" data-theme={theme}>
      <div className="topbar-left">
        {title    && <div className="topbar-title">{title}</div>}
        {subtitle && <div className="topbar-sub">{subtitle}</div>}
      </div>
      <div className="topbar-right">
        {actions}
        <div className="tog" onClick={toggle} data-theme={theme}>
          <span className="tog-icon">{isDark ? <MoonIcon/> : <SunIcon/>}</span>
          <div className="tog-track"><div className={`tog-knob${isDark?'':' on'}`}/></div>
          <span className="tog-lbl">{isDark ? 'Dark' : 'Light'}</span>
        </div>
        {/* Bell */}
        <div ref={notifRef} style={{position:'relative'}}>
          <div className="tb-bell" data-theme={theme} onClick={()=>{ setShowNotif(s=>!s); setShowProfile(false); }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            {unread > 0 && <div className="tb-bell-dot">{unread > 9 ? '9+' : unread}</div>}
          </div>
          {showNotif && <NotifPanel notifs={notifs} markRead={markRead} markAll={markAll} onClose={()=>setShowNotif(false)} theme={theme}/>}
        </div>
        {/* Avatar */}
        <div ref={profileRef} style={{position:'relative'}}>
          <div className="tb-av" data-theme={theme} onClick={()=>{ setShowProfile(s=>!s); setShowNotif(false); }}>{user.initials}</div>
          {showProfile && <ProfilePanel user={user} theme={theme} onClose={()=>setShowProfile(false)} navigate={navigate}/>}
        </div>
      </div>
    </div>
  );
}
